import React, { useState } from "react";
import {
  IconArrowsExchange,
  IconPlus,
  IconSearch,
  IconBuildingStore,
  IconMapPin,
  IconCheck,
  IconClock,
  IconTruckDelivery,
  IconUser,
  IconAlertCircle
} from "@tabler/icons-react";
import { useAssetTransfers, useAssets, transferAsset } from "../../controllers/assets.controller";
import toast from "react-hot-toast";

export default function AssetTransfersTab() {
  const [search, setSearch] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { data: transfers, error, isLoading, mutate } = useAssetTransfers({ transfer_type: "Transfer" });
  const { data: assets } = useAssets();

  const [formData, setFormData] = useState({
    asset_id: "",
    transfer_type: "Transfer",
    from_branch_id: "",
    to_branch_id: "",
    from_location: "",
    to_location: "",
    from_department: "",
    to_department: "Operations",
    from_custodian: "",
    to_custodian: "",
    transfer_date: new Date().toISOString().split("T")[0],
    reason: "Relocating for property upgrade",
    status: "Completed",
    handover_notes: ""
  });

  const transferList = Array.isArray(transfers)
    ? transfers.filter((t) => {
        if (!search) return true;
        const q = search.toLowerCase();
        return (
          t.asset_code?.toLowerCase().includes(q) ||
          t.asset_name?.toLowerCase().includes(q) ||
          t.from_location?.toLowerCase().includes(q) ||
          t.to_location?.toLowerCase().includes(q) ||
          t.to_department?.toLowerCase().includes(q) ||
          t.reason?.toLowerCase().includes(q)
        );
      })
    : [];

  const handleOpenTransferModal = () => {
    const firstAsset = assets?.[0];
    setFormData({
      asset_id: firstAsset?.id || "",
      transfer_type: "Transfer",
      from_branch_id: firstAsset?.branch_id || "",
      to_branch_id: firstAsset?.branch_id || "",
      from_location: firstAsset?.location || firstAsset?.room_number || "Central Store",
      to_location: "",
      from_department: firstAsset?.department || "General",
      to_department: "Operations",
      from_custodian: firstAsset?.assigned_to || firstAsset?.custodian || "",
      to_custodian: "",
      transfer_date: new Date().toISOString().split("T")[0],
      reason: "Relocation to new wing",
      status: "Completed",
      handover_notes: ""
    });
    setIsModalOpen(true);
  };

  const handleAssetSelect = (assetId) => {
    const asset = assets?.find((a) => String(a.id) === String(assetId));
    setFormData((prev) => ({
      ...prev,
      asset_id: assetId,
      from_branch_id: asset?.branch_id || "",
      from_location: asset?.location || asset?.room_number || "Central Store",
      from_department: asset?.department || "General",
      from_custodian: asset?.assigned_to || asset?.custodian || ""
    }));
  };

  const handleSubmitTransfer = async (e) => {
    e.preventDefault();
    if (!formData.asset_id) {
      toast.error("Please select an asset to transfer");
      return;
    }
    if (!formData.to_location && !formData.to_department) {
      toast.error("Please specify destination location or department");
      return;
    }

    try {
      await transferAsset(formData);
      toast.success("Asset transfer recorded successfully");
      setIsModalOpen(false);
      mutate();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Failed to record transfer");
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white dark:bg-gray-800 p-5 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm">
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <IconArrowsExchange className="w-6 h-6 text-restro-green" />
            Asset Transfers & Movement Log
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Track asset relocation across branches, buildings, floors, and rooms with full historical audit trails.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <IconSearch className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search transfer history..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 pr-4 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-restro-green w-64 text-gray-800 dark:text-gray-200"
            />
          </div>
          <button
            onClick={handleOpenTransferModal}
            className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-restro-green hover:bg-emerald-600 rounded-xl shadow-sm transition"
          >
            <IconPlus className="w-4 h-4" />
            Initiate Transfer
          </button>
        </div>
      </div>

      {/* Transfer History Table */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm text-gray-600 dark:text-gray-300">
            <thead className="bg-gray-50 dark:bg-gray-700/50 text-gray-700 dark:text-gray-200 uppercase text-xs font-semibold">
              <tr>
                <th className="px-6 py-4">Asset Details</th>
                <th className="px-6 py-4">From Location</th>
                <th className="px-6 py-4">To Destination</th>
                <th className="px-6 py-4">Custody Change</th>
                <th className="px-6 py-4">Transfer Date</th>
                <th className="px-6 py-4">Reason / Notes</th>
                <th className="px-6 py-4 text-center">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {isLoading ? (
                <tr>
                  <td colSpan="7" className="text-center py-10 text-gray-400">
                    Loading transfer logs...
                  </td>
                </tr>
              ) : transferList.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center py-12 text-gray-400">
                    <IconArrowsExchange className="w-12 h-12 mx-auto mb-2 opacity-30" />
                    No asset transfers recorded yet.
                  </td>
                </tr>
              ) : (
                transferList.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50/50 dark:hover:bg-gray-750 transition">
                    <td className="px-6 py-4">
                      <div className="font-semibold text-gray-900 dark:text-white">
                        {item.asset_name || "Asset #" + item.asset_id}
                      </div>
                      <div className="text-xs font-mono text-gray-400">
                        {item.asset_code || `AST-${item.asset_id}`}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-800 dark:text-gray-200 flex items-center gap-1">
                        <IconMapPin className="w-3.5 h-3.5 text-gray-400" />
                        {item.from_location || "Store / Central"}
                      </div>
                      {item.from_department && (
                        <div className="text-xs text-gray-400">{item.from_department}</div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="font-medium text-emerald-700 dark:text-emerald-400 flex items-center gap-1">
                        <IconMapPin className="w-3.5 h-3.5 text-emerald-500" />
                        {item.to_location || item.to_department}
                      </div>
                      {item.to_department && (
                        <div className="text-xs text-gray-400">{item.to_department}</div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        From: <span className="font-medium text-gray-700 dark:text-gray-300">{item.from_custodian || "Unassigned"}</span>
                      </div>
                      <div className="text-xs text-emerald-600 dark:text-emerald-400 mt-0.5">
                        To: <span className="font-semibold">{item.to_custodian || item.assigned_employee || "Unassigned"}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-xs">
                      {item.transfer_date ? new Date(item.transfer_date).toLocaleDateString() : "-"}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-xs font-medium text-gray-800 dark:text-gray-200">
                        {item.reason || "Relocation"}
                      </div>
                      {item.handover_notes && (
                        <div className="text-xs text-gray-400 truncate max-w-xs">{item.handover_notes}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800">
                        <IconCheck className="w-3 h-3" />
                        {item.status || "Completed"}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal: Initiate Transfer */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 overflow-y-auto">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-xl overflow-hidden border border-gray-100 dark:border-gray-700 my-8">
            <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <IconArrowsExchange className="w-5 h-5 text-restro-green" />
                Initiate Asset Transfer
              </h3>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleSubmitTransfer} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Select Asset to Transfer *
                </label>
                <select
                  value={formData.asset_id}
                  onChange={(e) => handleAssetSelect(e.target.value)}
                  className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-restro-green text-gray-800 dark:text-gray-200"
                  required
                >
                  <option value="">-- Choose asset --</option>
                  {assets?.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.asset_code} - {a.asset_name} (Current: {a.location || a.room_number || "Store"})
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50 dark:bg-gray-900 p-4 rounded-xl border border-gray-200 dark:border-gray-700">
                <div>
                  <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Current Location</div>
                  <div className="text-sm font-medium text-gray-800 dark:text-gray-200">
                    Location: {formData.from_location || "Store"}
                  </div>
                  <div className="text-xs text-gray-400 mt-1">
                    Dept: {formData.from_department || "General"}
                  </div>
                  <div className="text-xs text-gray-400 mt-1">
                    Custodian: {formData.from_custodian || "Unassigned"}
                  </div>
                </div>

                <div>
                  <div className="text-xs font-semibold text-emerald-600 uppercase mb-2">Destination</div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Target Location / Room *
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. Banquet Hall B or Room 301"
                      value={formData.to_location}
                      onChange={(e) => setFormData({ ...formData, to_location: e.target.value })}
                      className="w-full px-3 py-1.5 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-800 dark:text-gray-200"
                      required
                    />
                  </div>
                  <div className="mt-2">
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Target Department
                    </label>
                    <select
                      value={formData.to_department}
                      onChange={(e) => setFormData({ ...formData, to_department: e.target.value })}
                      className="w-full px-3 py-1.5 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-800 dark:text-gray-200"
                    >
                      <option value="Front Office">Front Office</option>
                      <option value="Housekeeping">Housekeeping</option>
                      <option value="Food & Beverage / Restaurant">Food & Beverage / Restaurant</option>
                      <option value="Kitchen / Culinary">Kitchen / Culinary</option>
                      <option value="Maintenance / Engineering">Maintenance / Engineering</option>
                      <option value="Administration / Management">Administration</option>
                      <option value="Security">Security</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                    New Custodian / Responsible Person
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Subash Karki"
                    value={formData.to_custodian}
                    onChange={(e) => setFormData({ ...formData, to_custodian: e.target.value })}
                    className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-restro-green text-gray-800 dark:text-gray-200"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                    Transfer Date *
                  </label>
                  <input
                    type="date"
                    value={formData.transfer_date}
                    onChange={(e) => setFormData({ ...formData, transfer_date: e.target.value })}
                    className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-restro-green text-gray-800 dark:text-gray-200"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Reason for Transfer *
                </label>
                <input
                  type="text"
                  placeholder="e.g. Reallocated for seasonal banquet operations"
                  value={formData.reason}
                  onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                  className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-restro-green text-gray-800 dark:text-gray-200"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Handover Notes / Verification Remarks
                </label>
                <textarea
                  rows="2"
                  placeholder="Notes regarding physical movement and initial testing at destination..."
                  value={formData.handover_notes}
                  onChange={(e) => setFormData({ ...formData, handover_notes: e.target.value })}
                  className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-restro-green text-gray-800 dark:text-gray-200"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 rounded-xl transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 text-sm font-semibold text-white bg-restro-green hover:bg-emerald-600 rounded-xl shadow-sm transition"
                >
                  Execute Transfer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
